import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Rating,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import MyLocationIcon from '@mui/icons-material/MyLocation';

// Configure axios defaults
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Fix for default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface SearchResult {
  place_id: string;
  display_name: string;
  lat: string;
  lon: string;
}

interface RestaurantFormData {
  name: string;
  latitude: number;
  longitude: number;
  address: string;
  cuisines: number[];
  rating: number;
  dishes: DishVote[];
}

interface DishVote {
  id: number;
  name: string;
  rating: number;
  hasVoted: boolean;
  isAvailable: boolean;
  cuisine?: number;
}

interface Cuisine {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
}

interface Dish {
  id: number;
  name: string;
  description: string;
  cuisine: number;
  cuisine_name: string;
  votes_count: number;
  has_voted: boolean;
}

const initialFormData: RestaurantFormData = {
  name: '',
  latitude: 0,
  longitude: 0,
  address: '',
  cuisines: [],
  rating: 0,
  dishes: [],
};

const MapControls: React.FC<{
  onLocationFound: (lat: number, lng: number) => void;
  onSearchSelect: (result: SearchResult | null) => void;
}> = ({ onLocationFound, onSearchSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const map = useMap();

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`
      );
      setSearchResults(response.data);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching location:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleLocationClick = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          map.setView([latitude, longitude], 15);
          onLocationFound(latitude, longitude);
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Unable to get your location. Please check your location settings.');
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    } else {
      alert('Geolocation is not supported by your browser');
    }
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 1000,
        backgroundColor: 'white',
        padding: 1,
        borderRadius: 1,
        boxShadow: 1,
        width: '300px',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search address..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            handleSearch(e.target.value);
          }}
        />
        <Button
          variant="contained"
          onClick={handleLocationClick}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <MyLocationIcon />
        </Button>
      </Box>
      {showSearchResults && searchResults.length > 0 && (
        <Paper
          sx={{
            maxHeight: '200px',
            overflow: 'auto',
            mt: 1,
          }}
        >
          <List dense>
            {searchResults.map((result) => (
              <ListItem
                key={result.place_id}
                button
                onClick={() => {
                  const lat = parseFloat(result.lat);
                  const lng = parseFloat(result.lon);
                  map.setView([lat, lng], 15);
                  onSearchSelect(result);
                  setSearchQuery(result.display_name);
                  setShowSearchResults(false);
                }}
              >
                <ListItemText
                  primary={result.display_name}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

const LocationMarker: React.FC<{
  position: [number, number];
  onPositionChange: (lat: number, lng: number, address?: string) => void;
}> = ({ position, onPositionChange }) => {
  const markerRef = useRef<L.Marker>(null);
  const map = useMap();

  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setLatLng(position);
    }
  }, [position]);

  const eventHandlers = useCallback(() => {
    const marker = markerRef.current;
    if (marker) {
      marker.on('dragend', async () => {
        const newPosition = marker.getLatLng();
        try {
          const response = await axios.get(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${newPosition.lat}&lon=${newPosition.lng}`
          );
          onPositionChange(newPosition.lat, newPosition.lng, response.data.display_name);
        } catch (error) {
          console.error('Error getting address:', error);
          onPositionChange(newPosition.lat, newPosition.lng);
        }
      });
    }
  }, [onPositionChange]);

  useEffect(() => {
    eventHandlers();
  }, [eventHandlers]);

  return (
    <Marker
      ref={markerRef}
      position={position}
      draggable={true}
    />
  );
};

const AddFavoriteRestaurant: React.FC = () => {
  const navigate = useNavigate();
  const { token, user } = useSelector((state: RootState) => state.auth);
  const [formData, setFormData] = useState<RestaurantFormData>(initialFormData);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLocating, setIsLocating] = useState<boolean>(false);
  const [cuisines, setCuisines] = useState<Cuisine[]>([]);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [showDishSelection, setShowDishSelection] = useState<boolean>(false);
  const [mapKey, setMapKey] = useState<number>(0);

  // Redirect if not authenticated
  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  // Fetch cuisines
  useEffect(() => {
    const fetchCuisines = async () => {
      try {
        if (!token) {
          setError('Please log in to access this feature');
          return;
        }

        console.log('Fetching cuisines...');
        const cuisinesResponse = await axios.get('/api/cuisines/', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        console.log('Cuisines response:', cuisinesResponse.data);
        
        if (cuisinesResponse.data && Array.isArray(cuisinesResponse.data.results)) {
          setCuisines(cuisinesResponse.data.results);
          console.log('Cuisines set:', cuisinesResponse.data.results);
        }
      } catch (err: any) {
        console.error('Error fetching cuisines:', err);
        if (err.response?.status === 401) {
          setError('Please log in to access this feature');
          navigate('/login');
        } else {
          setError('Failed to fetch cuisines');
        }
      }
    };

    if (token) {
      fetchCuisines();
    }
  }, [token, navigate]);

  // Get location on component mount
  useEffect(() => {
    const getCurrentLocation = () => {
      if (navigator.geolocation) {
        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setFormData(prev => ({
              ...prev,
              latitude,
              longitude
            }));
            setMapKey(prev => prev + 1);
            setIsLocating(false);
          },
          (error) => {
            console.error('Error getting location:', error);
            setIsLocating(false);
            // Set default location to Istanbul if geolocation fails
            setFormData(prev => ({
              ...prev,
              latitude: 41.0082,
              longitude: 28.9784
            }));
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          }
        );
      } else {
        // Set default location to Istanbul if geolocation is not supported
        setFormData(prev => ({
          ...prev,
          latitude: 41.0082,
          longitude: 28.9784
        }));
      }
    };

    getCurrentLocation();
  }, []);

  const MapEvents = () => {
    useMapEvents({
      click: (e) => {
        setFormData(prev => ({
          ...prev,
          latitude: e.latlng.lat,
          longitude: e.latlng.lng,
        }));
        // Get address for clicked location
        getAddressFromCoordinates(e.latlng.lat, e.latlng.lng);
      },
    });
    return null;
  };

  const CurrentLocationMarker = () => {
    const map = useMap();

    useEffect(() => {
      if (formData.latitude && formData.longitude) {
        map.setView([formData.latitude, formData.longitude], 15);
      }
    }, [formData.latitude, formData.longitude, map]);

    return null;
  };

  const getAddressFromCoordinates = async (lat: number, lon: number) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`
      );
      setFormData(prev => ({
        ...prev,
        address: response.data.display_name || '',
      }));
    } catch (err) {
      console.error('Error getting address:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCuisineChange = async (event: any) => {
    const selectedCuisineId = event.target.value;
    console.log('Selected cuisine ID:', selectedCuisineId);
    
    const selectedCuisine = cuisines.find(c => c.id === selectedCuisineId);
    console.log('Selected cuisine:', selectedCuisine);
    
    try {
      const response = await axios.get(`/api/dishes/`, {
        params: {
          cuisine_id: selectedCuisineId
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const dishes: Dish[] = response.data.results || [];
      const dishVotes = dishes.map(dish => ({
        id: dish.id,
        name: dish.name,
        rating: 0,
        hasVoted: false,
        isAvailable: false,
        cuisine: dish.cuisine
      }));
      
      console.log('Initial dishes created:', dishVotes);

      setFormData(prev => ({
        ...prev,
        cuisines: [selectedCuisineId],
        dishes: dishVotes
      }));

      // Always show dish selection dialog when a cuisine is selected
      setShowDishSelection(true);
    } catch (error) {
      console.error('Error fetching dishes:', error);
    }
  };

  const handleRatingChange = (_event: React.SyntheticEvent, newValue: number | null) => {
    if (newValue !== null) {
      setFormData(prev => ({
        ...prev,
        rating: newValue
      }));
    }
  };

  const handleDishRatingChange = (dishName: string, newValue: number | null) => {
    if (newValue !== null) {
      setFormData(prev => ({
        ...prev,
        dishes: prev.dishes.map(dish => 
          dish.name === dishName 
            ? { ...dish, rating: newValue, hasVoted: true }
            : dish
        )
      }));
    }
  };

  const handleDishAvailabilityChange = (dishName: string) => {
    setFormData(prev => ({
      ...prev,
      dishes: prev.dishes.map(dish => 
        dish.name === dishName 
          ? { ...dish, isAvailable: !dish.isAvailable }
          : dish
      )
    }));
  };

  const handleDishSelectionClose = () => {
    setShowDishSelection(false);
  };

  const handleDishSelectionConfirm = () => {
    setShowDishSelection(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Format the token with Bearer prefix
      const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

      // Prepare the restaurant data
      const restaurantData = {
        name: formData.name,
        description: `${formData.name} - A great place to eat!`,
        latitude: formData.latitude,
        longitude: formData.longitude,
        address: formData.address,
        cuisine_ids: formData.cuisines,
        price_range: 2,
        is_open: true,
        tags: []
      };

      // Create the restaurant
      const restaurantResponse = await axios.post(
        '/api/restaurants/',
        restaurantData,
        {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json'
          }
        }
      );

      const restaurantId = restaurantResponse.data.id;

      // Create a review for the restaurant if rating is provided
      if (formData.rating > 0) {
        try {
          await axios.post(
            `/api/restaurants/${restaurantId}/rate/`,
            { rating: formData.rating },
            {
              headers: {
                'Authorization': authToken,
                'Content-Type': 'application/json'
              }
            }
          );
        } catch (reviewError: any) {
          console.error('Error creating review:', reviewError);
          // Continue with dish creation even if review creation fails
        }
      }

      // Only add dishes that are marked as available
      const selectedDishes = formData.dishes.filter(dish => dish.isAvailable);
      
      // Add the selected dishes to the restaurant and create dish ratings
      for (const dish of selectedDishes) {
        try {
          // Add dish to restaurant
          await axios.post(
            `/api/restaurants/${restaurantId}/add_dish/`,
            {
              dish_id: dish.id
            },
            {
              headers: {
                'Authorization': authToken,
                'Content-Type': 'application/json'
              }
            }
          );

          // Create dish rating if rating is provided
          if (dish.rating > 0) {
            await axios.post(
              `/api/restaurants/${restaurantId}/dishes/${dish.id}/rate/`,
              { rating: dish.rating },
              {
                headers: {
                  'Authorization': authToken,
                  'Content-Type': 'application/json'
                }
              }
            );
          }
        } catch (dishError: any) {
          console.error(`Error adding dish ${dish.id}:`, dishError);
          // Continue with other dishes even if one fails
        }
      }

      setSuccess('Restaurant added successfully!');
      setTimeout(() => {
        navigate('/nearby');
      }, 2000);
    } catch (err: any) {
      console.error('Error adding restaurant:', err);
      if (err.response?.status === 401) {
        setError('Please log in to access this feature');
        navigate('/login');
      } else {
        setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to add restaurant');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, pb: 8 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Add Favorite Restaurant
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Restaurant Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ height: 400, position: 'relative', mb: 2 }}>
                {isLocating ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <MapContainer
                    key={mapKey}
                    center={[formData.latitude || 41.0082, formData.longitude || 28.9784]}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <MapControls
                      onLocationFound={(lat, lng) => {
                        setFormData(prev => ({
                          ...prev,
                          latitude: lat,
                          longitude: lng
                        }));
                        setMapKey(prev => prev + 1);
                      }}
                      onSearchSelect={(result) => {
                        if (result) {
                          const lat = parseFloat(result.lat);
                          const lng = parseFloat(result.lon);
                          setFormData(prev => ({
                            ...prev,
                            latitude: lat,
                            longitude: lng,
                            address: result.display_name
                          }));
                          setMapKey(prev => prev + 1);
                        }
                      }}
                    />
                    <LocationMarker
                      position={[formData.latitude || 41.0082, formData.longitude || 28.9784]}
                      onPositionChange={(lat, lng, address) => {
                        setFormData(prev => ({
                          ...prev,
                          latitude: lat,
                          longitude: lng,
                          ...(address && { address })
                        }));
                      }}
                    />
                  </MapContainer>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Cuisine</InputLabel>
                <Select
                  value={formData.cuisines[0] || ''}
                  onChange={handleCuisineChange}
                  label="Cuisine"
                  required
                >
                  {cuisines.map((cuisine) => (
                    <MenuItem key={cuisine.id} value={cuisine.id}>
                      {cuisine.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography component="legend">Restaurant Rating</Typography>
              <Rating
                name="rating"
                value={formData.rating}
                onChange={handleRatingChange}
                precision={0.5}
                max={5}
              />
            </Grid>

            {formData.cuisines[0] && formData.dishes.some(dish => dish.isAvailable) && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Rate Available Dishes
                </Typography>
                <List>
                  {formData.dishes
                    .filter(dish => dish.isAvailable)
                    .map((dish, index, filteredDishes) => (
                      <React.Fragment key={dish.name}>
                        <ListItem>
                          <ListItemText
                            primary={dish.name}
                            secondary={
                              <Rating
                                value={dish.rating}
                                onChange={(_, value) => handleDishRatingChange(dish.name, value)}
                                precision={0.5}
                                max={5}
                                icon={<ThumbUpIcon fontSize="inherit" />}
                                emptyIcon={<ThumbUpOutlinedIcon fontSize="inherit" />}
                              />
                            }
                          />
                        </ListItem>
                        {index < filteredDishes.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                </List>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Add Restaurant'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Dialog 
        open={showDishSelection} 
        onClose={handleDishSelectionClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Available Dishes</DialogTitle>
        <DialogContent>
          {formData.dishes.length === 0 ? (
            <Typography>No dishes available for this cuisine.</Typography>
          ) : (
            <FormGroup>
              {formData.dishes.map((dish) => (
                <FormControlLabel
                  key={dish.name}
                  control={
                    <Checkbox
                      checked={dish.isAvailable}
                      onChange={() => handleDishAvailabilityChange(dish.name)}
                    />
                  }
                  label={dish.name}
                />
              ))}
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDishSelectionClose}>Cancel</Button>
          <Button onClick={handleDishSelectionConfirm} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddFavoriteRestaurant; 